/**
 * Legend - Explains what each button option means
 * Displayed as an anchored box to the right of the question card
 */

import React from 'react';
import { colors, fonts, spacing, radii } from '../styles/theme';

interface LegendProps {
    hasExample?: boolean;
    /** When true (narrow viewports), legend spans full width below the question card */
    stackBelow?: boolean;
}

const Legend: React.FC<LegendProps> = ({ hasExample = false, stackBelow = false }) => {
    return (
        <div style={{
            ...styles.legend,
            ...(stackBelow ? styles.legendStacked : {}),
        }}>
            <div style={styles.legendItem}>
                <span style={{ ...styles.legendButton, ...styles.legendYes }}>Yes</span>
                <span style={styles.legendText}>You have good control over this aspect and are confident in your current approach.</span>
            </div>
            <div style={styles.legendItem}>
                <span style={{ ...styles.legendButton, ...styles.legendBag }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: spacing[1] }}>
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    Add to bag
                </span>
                <span style={styles.legendText}>You are unsure, or this is a concern you would like support with. The item will be added to your bag with guidance.</span>
            </div>
            <div style={styles.legendItem}>
                <span style={{ ...styles.legendButton, ...styles.legendNo }}>No</span>
                <span style={styles.legendText}>This is not relevant to your project or situation.</span>
            </div>
            {hasExample && (
                <div style={styles.legendItem}>
                    <span style={styles.legendExampleLabel}>Example</span>
                    <span style={styles.legendText}>A concrete example from your chosen application area is shown below each question.</span>
                </div>
            )}
            <div style={styles.legendNote}>
                At the end of the session, you will receive a summary with best practices for each item in your bag.
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    legend: {
        backgroundColor: colors.bgLight,
        borderRadius: radii.md,
        padding: spacing[4],
        border: `1px solid ${colors.border}`,
        width: '310px',
        flexShrink: 0,
        position: 'sticky' as const,
        top: spacing[6],
        alignSelf: 'flex-start',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[2],
        marginBottom: spacing[3],
    },
    legendButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[1]} ${spacing[2]}`,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        borderRadius: radii.sm,
        minWidth: '60px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    legendYes: {
        backgroundColor: colors.yes,
        color: colors.white,
    },
    legendNo: {
        backgroundColor: colors.no,
        color: colors.white,
    },
    legendBag: {
        backgroundColor: colors.cart,
        color: colors.white,
        display: 'inline-flex',
        alignItems: 'center',
    },
    legendText: {
        fontSize: fonts.sm,
        color: colors.textSecondary,
        lineHeight: fonts.lineHeightRelaxed,
        flex: 1,
    },
    legendExampleLabel: {
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        color: colors.primary,
        minWidth: '100px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    legendNote: {
        fontSize: fonts.sm,
        color: colors.textMuted,
        fontStyle: 'italic',
        marginTop: spacing[2],
        paddingTop: spacing[3],
        borderTop: `1px solid ${colors.border}`,
        textAlign: 'center',
    },
    legendStacked: {
        width: '100%',
        maxWidth: '100%',
        flexShrink: 1,
        position: 'static' as const,
        alignSelf: 'stretch' as const,
    },
};

export default Legend;
